import { useNavigate } from "react-router-dom";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Check, ChevronDown, LayoutGrid } from "lucide-react";
import { toast } from "sonner";
import { useStore } from "@/domain/store";
import { workspaces } from "@/domain/seed/workspaces";
import { useActiveWorkspace, useActiveProfile } from "@/domain/hooks";

export function WorkspaceSwitcher() {
  const active = useActiveWorkspace();
  const profile = useActiveProfile();
  const setWorkspace = useStore((s) => s.setWorkspace);
  const navigate = useNavigate();

  const portalLanding =
    profile.group === "funder" ? "/funder" : profile.group === "oversight" ? "/stratus" : profile.landing;

  function choose(id: string, name: string) {
    if (id === active.id) return;
    setWorkspace(id);
    toast.success(`Switched workspace to ${name}`);
    navigate(`/workspace/${id}`);
  }

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button className="flex items-center gap-1.5 rounded-md border border-line bg-card px-2.5 py-1 text-xs font-medium text-ink transition-colors hover:bg-subtle">
          <span
            className="h-2 w-2 shrink-0 rounded-full"
            style={{ background: `hsl(${active.accentHsl})` }}
            aria-hidden
          />
          {/* name hidden on mobile — colour dot + chevron is enough to tap */}
          <span className="hidden max-w-[140px] truncate sm:inline">{active.programmeName}</span>
          <ChevronDown className="h-3 w-3 shrink-0 text-faint" />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={6}
          className="z-50 w-72 rounded-lg border border-line bg-card p-1 shadow-overlay animate-fade-in"
        >
          <DropdownMenu.Item
            onSelect={() => navigate(portalLanding)}
            className="flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-2 text-sm font-medium text-ink outline-none data-[highlighted]:bg-subtle"
          >
            <LayoutGrid className="h-4 w-4 shrink-0 text-mute" aria-hidden />
            <span className="min-w-0 flex-1 truncate">All deals</span>
          </DropdownMenu.Item>
          <DropdownMenu.Separator className="my-1 h-px bg-line" />
          <DropdownMenu.Label className="px-2 py-1.5 text-xs font-semibold uppercase tracking-wide text-faint">
            Workspaces
          </DropdownMenu.Label>
          {workspaces.map((w) => (
            <DropdownMenu.Item
              key={w.id}
              onSelect={() => choose(w.id, w.programmeName)}
              className="flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-2 text-sm text-ink outline-none data-[highlighted]:bg-subtle"
            >
              <span
                className="h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ background: `hsl(${w.accentHsl})` }}
                aria-hidden
              />
              <span className="min-w-0 flex-1 truncate">{w.programmeName}</span>
              {w.id === active.id && <Check className="h-4 w-4 text-brand" />}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
